import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { zip, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { SpeechToTextService } from '@privatelibdev/speech-to-text';
import { NoteTemplateListingService } from '../../services/note-template-listing/note-template-listing.service';
import { ITab, TemplateListing } from '../../models/manage-template.interface';
import { ITemplateList, TemplateCategory } from '../../models/template.model';
import { MatDialog } from '@angular/material/dialog';
import { TemplateListingComponent } from '../../components/manage-template/template-listing/template-listing.component';
import { ReferralDialogComponent, ReferralDialogResult } from '../referral-dialog/referral-dialog.component';
import { soapTemplateType, SubSectionTitle, TableType } from '../../util/soap-config.constants';
import { SoapServiceService } from '../../services/soap-service/soap-service.service';
import { GeneratedSoapNoteResponse, VerticalUserInputs, Vitals, GenerateVitalsResponse } from '../../models/dictation.interface';
import { CVA_CONSTANTS } from '../../util/constants';
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { PractitionerService } from '../../services/practitioner/practitioner.service';
import { UserPreferenceService } from '../../services/user-preferences/user-preference.service';
import { CVA_AUTH_MODULES } from '../../models/auth.modules.enum';

@Component({
  standalone: false,
  selector: 'cva-generate-button',
  templateUrl: './generate-button.component.html',

})
export class GenerateButtonComponent implements OnInit, OnChanges, OnDestroy {
  @Input() patientHistory: number | string = 0;
  @Input() name: string = "Generate";
  @Input() noteTemplateListing: TemplateListing[] = [];
  @Output() actionClick: EventEmitter<ITab> = new EventEmitter<ITab>()
  @Input() hasDefaultTemplate: boolean;
  @Input() DEFAULT_PREFERRED_TEMPLATE: string;
  @Input() transcription: string = '';
  @Input() appointmentRefId!: string;
  @Input() vitals: VerticalUserInputs[] = [];
  @Input() disabled = false;
  v1TemplateIdentifier: string[] = CVA_CONSTANTS.V1_TEMPLATE_IDENTIFIERS;
  templateType: string = soapTemplateType.Lite;
  referenceId: string | undefined;
  groupedTemplateListing: ITemplateList[];
  permissionType: string = CVA_CONSTANTS.PERMISSION.WRITE;
  moduleName: string = CVA_AUTH_MODULES.CVA_SOAP_NOTES;
  private destroyed = false;

  constructor(public speechToTextService: SpeechToTextService,
    private readonly noteTemplateListingService: NoteTemplateListingService,
    private readonly matDialog: MatDialog,
    private readonly soapServiceService: SoapServiceService,
    private readonly cvaNotificationService: CVANotificationService,
    public practitionerService: PractitionerService,
    private readonly userPreferenceService: UserPreferenceService,
  ) {
    this.referenceId = this.practitionerService?.loggedInPractitioner?.referenceId ?? history.state?.practitioner?.referenceId;
  }

  ngOnInit() {
    this.getTemplateSetting();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["transcription"]) {
      this.transcription = changes["transcription"].currentValue;
    }
  }

  getTemplateSetting() {
    if (this.referenceId) {
      this.userPreferenceService.getSoapSettings(this.referenceId, CVA_CONSTANTS.CONSTS.template_type)
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        .subscribe((res: any) => {
          const result = this.soapServiceService.getTemplate(res?.preference?.templateType, this.noteTemplateListing);
          if (result) {
            this.templateType = result.templateType;
          }
        })
    }
  }

  generateMore(event?: MouseEvent, isToStopRecording: boolean = false) {
    if (this.destroyed) return;
    if (this.transcription?.trim().length) {
      event?.stopPropagation();
      if (isToStopRecording) {
        this.stopRecording();
      }

      this.groupedTemplateListing = this.noteTemplateListingService.groupTemplatesByCategoryAndFavorites(
        JSON.parse(JSON.stringify(this.noteTemplateListing))
      );
      if (this.hasDefaultTemplate || !this.DEFAULT_PREFERRED_TEMPLATE) {
        this.openTemplateSelection();
      } else {
        //hit the default template
        const template: TemplateListing | undefined = this.getTemplate(this.templateType);
        if (!template) return
        if (template.identifier === 'referral') {
          this.openReferralDialog(template);
        } else if (this.v1TemplateIdentifier.includes(this.templateType) || template.category !== TemplateCategory.NOTE) {
          this.generateSoapNotes(template);
        } else {
          this.generateSoapNotesV2(template);

        }
      }
    }

  }

  stopRecording() {
    this.speechToTextService.stopRec.next("stopped");
  }

  openTemplateSelection() {
    this.matDialog
      .open(TemplateListingComponent, {
        autoFocus: false,
        data: {
          defaultPreferredTemplate: this.DEFAULT_PREFERRED_TEMPLATE,
          noteTemplateListing: this.groupedTemplateListing
        },
        width: '40rem',
        height: 'auto',
        maxHeight: '90vh',
      })
      .afterClosed()
      .subscribe((identifier: string) => {
        if (identifier) {
          //fetch the requested template
          const template: TemplateListing | undefined = this.getTemplate(identifier);
          if (!template) return
          if (identifier === 'referral') {
            this.openReferralDialog(template);
          } else if (this.v1TemplateIdentifier.includes(identifier) || template.category !== TemplateCategory.NOTE) {
            this.generateSoapNotes(template);
          } else {
            this.generateSoapNotesV2(template);
          }
        }
      });
  }

  openReferralDialog(template: TemplateListing): void {
    this.matDialog
      .open(ReferralDialogComponent, {
        autoFocus: false,
        data: { patientHistory: this.patientHistory, transcription: this.transcription },
        width: '40rem',
        height: 'auto',
        maxHeight: '95vh',
        enterAnimationDuration: '300ms',
        exitAnimationDuration: '200ms'
      })
      .afterClosed()
      .subscribe((result: ReferralDialogResult | undefined) => {
        if (result) {
          // User clicked proceed with selected options
          console.log('Selected classifications:', result);
          let patientHistory = 0;
          if (result.includepatientHistory) {
            patientHistory = typeof this.patientHistory === 'string' ? parseInt(this.patientHistory) : this.patientHistory;
          }
          // Pass only the display property as array
          this.generateSoapNotes(template, patientHistory, result.selectedClassifications);
        } else {
          // User clicked cancel or closed the dialog
          console.log('Referral dialog cancelled');
        }
      });
  }

  getTemplate(identifier: string): TemplateListing | undefined {
    this.groupedTemplateListing = this.noteTemplateListingService.groupTemplatesByCategoryAndFavorites(
      JSON.parse(JSON.stringify(this.noteTemplateListing))
    );
    const template = this.noteTemplateListing
      .find(template => template.identifier === identifier);
    return template;
  }

  extractFirstNumber(s: string) {
    const match = /(\d+(\.\d+)?)/.exec(s);
    return match ? parseFloat(match[0]) : null;
  }

  validateValue(text: string): string {
    const stringValue = this.extractFirstNumber(text);
    // Return the string value if it doesn't match the constant, otherwise return an empty string
    return stringValue && stringValue !== null ? String(stringValue) : ""
  }

  getVitalData(obj: Vitals, userInputs: VerticalUserInputs[]): { userInputs: VerticalUserInputs[], valuePresent: boolean } {
    let valuePresent = false;
    userInputs.forEach((userInput) => {
      switch (userInput.title) {
        case "Temperature":
          userInput.value = this.validateValue(obj?.Body_Temperature)
          break;
        case "Blood Pressure": {
          const Systolic_Blood_Pressure = parseInt(this.validateValue(obj?.Systolic_Blood_Pressure));
          const Diastolic_Blood_Pressure = parseInt(this.validateValue(obj?.Diastolic_Blood_Pressure));
          if (Systolic_Blood_Pressure && Diastolic_Blood_Pressure) {
            userInput.value = `${Systolic_Blood_Pressure}/${Diastolic_Blood_Pressure}`;
          }
          break;
        }
        case "Heart Rate":
          userInput.value = this.validateValue(obj?.Heart_Rate)
          break;
        case "Oxygen":
          userInput.value = this.validateValue(obj?.Oxygen_Saturation)
          break;
        case "Height":
          userInput.value = this.validateValue(obj?.Body_Height)
          break;
        case "Weight":
          userInput.value = this.validateValue(obj?.Body_Weight)
          break;
        case "Body Mass Index":
          userInput.value = this.validateValue(obj?.Body_Mass_Index)
          break;
        case "Respiratory Rate":
          userInput.value = this.validateValue(obj?.Respiratory_Rate)
          break;
        default:
          break;
      }
      if (!valuePresent && userInput.value != "") {
        valuePresent = true;
      }
    })
    return {
      userInputs,
      valuePresent
    };
  }

  generateSoapNotes(template: TemplateListing, patientHistory: number = 0, classifications: string[] = []) {
    const title = template.name;
    // Get patientId from localStorage (from currentAppointment)
    let patientId = '';
    try {
      const appointmentInfo = JSON.parse(
        localStorage.getItem('currentAppointment') || '{}'
      );

      patientId = appointmentInfo?.patient?.referenceId || '';

      // Fallback to patientId from localStorage
      if (!patientId) {
        patientId = localStorage.getItem('patientId') || '';
      }
    } catch {
      patientId = localStorage.getItem('patientId') || '';
    }

    this.soapServiceService
      .getAISoapNotes(
        template,
        template.identifier,
        this.transcription,
        undefined, // speechServiceTime
        undefined, // patientName
        undefined, // practitionerName
        patientId,
        patientHistory,
        classifications
      )
      .subscribe({
        next: (data: GeneratedSoapNoteResponse) => {
          if (!data) {
            throw new Error(CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR);
          }
          try {
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            const classicData = data["custom"] as { notes: string, vitals: any };
            let getVitalDataResult: { userInputs: VerticalUserInputs[], valuePresent: boolean } = { userInputs: [], valuePresent: false };
            const vitalsObj = JSON.parse(JSON.stringify(this.vitals)) as VerticalUserInputs[];
            vitalsObj.forEach(vital => {
              vital.value = '';
            });
            if (classicData.vitals) {
              getVitalDataResult = this.getVitalData(classicData.vitals as Vitals, vitalsObj);
            }
            this.soapServiceService.setAppointmentAndDraftedNotes(
              this.appointmentRefId,
              {
                [template.identifier]: {
                  section: {
                    type: TableType.vertical,
                    name: SubSectionTitle.VITAL_SIGNS,
                    header: SubSectionTitle.VITAL_SIGNS,
                  },
                  notes: classicData?.notes || data["custom"] as string,
                  userInputs: getVitalDataResult.userInputs.length > 0 ? getVitalDataResult.userInputs : vitalsObj
                }
              },
              this.transcription,
              template.identifier
            );
            //emit to switch to SOAP tab
            const addNewTab = {
              title,
              controlName: template.identifier,
              isEditable: true,
              identifier: template.identifier
            };
            this.actionClick.emit(addNewTab);
          } catch (error) {
            console.error("Found an error while parsing the soap notes response:", error);
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR,
              NotificationClassEnum.ERROR
            );
          }
        },
        error: (e) => {
          console.log("SOAP notes generation failed", e);
          const errorMessage = e.errorMessage || CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR;
          this.cvaNotificationService.showNotification(
            errorMessage,
            NotificationClassEnum.ERROR
          );
        },
      });
  }

  generateSoapNotesV2(template: TemplateListing) {
    const title = template.name;

    // shareReplay(1) ensures a single HTTP call even though notes$ is subscribed twice
    const notes$ = this.soapServiceService.getAISoapNotes(
      template,
      template.identifier,
      this.transcription,
    ).pipe(shareReplay(1));

    const vitals$ = this.soapServiceService.generateVitals(this.transcription).pipe(
      catchError((vitalsError) => {
        console.log("Vitals generation failed", vitalsError);
        this.cvaNotificationService.showNotification(
          CVA_CONSTANTS.MESSAGE.GENERATE_VITALS_ERR,
          NotificationClassEnum.ERROR
        );
        return of(null);
      }),
      shareReplay(1)
    );

    // Show notes immediately as soon as notes API responds — do not wait for vitals
    notes$.subscribe({
      next: (data: GeneratedSoapNoteResponse) => {
        if (!data) {
          throw new Error(CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR);
        }
        try {
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          const classicData = data["custom"] as { notes: string };
          const vitalsObj = JSON.parse(JSON.stringify(this.vitals)) as VerticalUserInputs[];
          vitalsObj.forEach(vital => {
            vital.value = '';
          });
          this.soapServiceService.setAppointmentAndDraftedNotes(
            this.appointmentRefId,
            {
              [template.identifier]: {
                section: {
                  type: TableType.vertical,
                  name: SubSectionTitle.VITAL_SIGNS,
                  header: SubSectionTitle.VITAL_SIGNS,
                },
                notes: classicData?.notes || data["custom"] as string,
                userInputs: vitalsObj
              }
            },
            this.transcription,
            template.identifier
          );
          const addNewTab = {
            title,
            controlName: template.identifier,
            isEditable: true,
            identifier: template.identifier
          };
          this.actionClick.emit(addNewTab);
        } catch (error) {
          console.error("Found an error while parsing the soap notes response:", error);
          this.cvaNotificationService.showNotification(
            CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR,
            NotificationClassEnum.ERROR
          );
        }
      },
      error: (e) => {
        console.log("SOAP notes generation failed", e);
        const errorMessage = e.errorMessage || CVA_CONSTANTS.MESSAGE.GENERATE_SOAP_NOTE_ERR;
        this.cvaNotificationService.showNotification(
          errorMessage,
          NotificationClassEnum.ERROR
        );
      },
    });

    // Update vitals once both APIs have responded.
    // zip buffers whichever arrives first and waits for the other.
    // If notes failed, zip errors and is silently ignored (already handled above).
    zip(notes$, vitals$).subscribe({
      next: ([data, vitalsResponse]: [GeneratedSoapNoteResponse, GenerateVitalsResponse | null]) => {
        if (!vitalsResponse) return;
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const classicData = data["custom"] as { notes: string };
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const vitalsData = vitalsResponse.vitals as any;
        if (vitalsData) {
          const vitalsObj = JSON.parse(JSON.stringify(this.vitals)) as VerticalUserInputs[];
          vitalsObj.forEach(vital => {
            vital.value = '';
          });
          const getVitalDataResult = this.getVitalData(vitalsData, vitalsObj);
          this.soapServiceService.setAppointmentAndDraftedNotes(
            this.appointmentRefId,
            {
              [template.identifier]: {
                section: {
                  type: TableType.vertical,
                  name: SubSectionTitle.VITAL_SIGNS,
                  header: SubSectionTitle.VITAL_SIGNS,
                },
                notes: classicData?.notes || data["custom"] as string,
                userInputs: getVitalDataResult.userInputs
              }
            },
            this.transcription,
            template.identifier
          );
          this.soapServiceService.vitalsUpdated$.next({
            appointmentRefId: this.appointmentRefId,
            vitals: getVitalDataResult.userInputs,
            identifier: template.identifier
          });
        }
      },
      // notes failed — already handled in the notes$ subscription above
      error: () => { }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.matDialog?.closeAll();
  }
}
